import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import JsonLd from '@/components/base/JsonLd';
import { comics } from '@/mocks/comics';

type TabKey = 'hot' | 'rating' | 'finished';

const TABS: { key: TabKey; label: string; desc: string }[] = [
  { key: 'hot', label: '人气榜', desc: '根据阅读热度与收藏综合排名' },
  { key: 'rating', label: '高分榜', desc: '读者评分最高的恋爱漫画' },
  { key: 'finished', label: '完结榜', desc: '一次性看完的完结佳作' },
];

export default function Ranking() {
  const [tab, setTab] = useState<TabKey>('hot');

  const list = useMemo(() => {
    let arr = [...comics];
    if (tab === 'hot') arr = arr.sort((a, b) => parseFloat(b.views) - parseFloat(a.views));
    if (tab === 'rating') arr = arr.sort((a, b) => b.rating - a.rating);
    if (tab === 'finished') arr = arr.filter((c) => c.status === '完结').sort((a, b) => b.rating - a.rating);
    return arr.slice(0, 10);
  }, [tab]);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: '桃花漫画网排行榜',
    description: '桃花漫画网恋爱漫画人气榜、高分榜、完结榜，热门言情漫画推荐。',
    url: 'https://cartonplay.github.io/ranking',
    inLanguage: 'zh-CN',
    isPartOf: { '@type': 'WebSite', name: '桃花漫画网', url: 'https://cartonplay.github.io/' },
  };

  return (
    <>
      <JsonLd data={schema} />
      <section className="mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-14">
        <nav aria-label="面包屑" className="mb-6 flex items-center gap-1.5 text-xs text-foreground-400">
          <Link to="/" className="hover:text-primary-500">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-600">排行榜</span>
        </nav>

        <header className="mb-8">
          <h1 className="font-display text-2xl font-bold text-foreground-900 md:text-4xl">
            桃花漫画排行榜
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            人气、评分、完结三大榜单，帮你快速找到值得一追的恋爱漫画。
          </p>
        </header>

        {/* Tabs */}
        <div className="inline-flex items-center rounded-full bg-background-100 p-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`whitespace-nowrap rounded-full px-5 py-2 text-sm transition-colors ${
                tab === t.key
                  ? 'bg-primary-500 font-medium text-background-50'
                  : 'text-foreground-600 hover:text-primary-600'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <p className="mt-3 text-xs text-foreground-400">
          {TABS.find((t) => t.key === tab)?.desc}
        </p>

        {/* List */}
        <ol className="mt-6 divide-y divide-background-200 overflow-hidden rounded-2xl bg-background-100/60">
          {list.map((c, i) => (
            <li key={c.id}>
              <Link to={`/comic/${c.id}`} className="group flex items-center gap-4 px-4 py-4 transition-colors hover:bg-background-50 md:px-6">
                <span
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl font-display text-lg font-bold ${
                    i === 0
                      ? 'bg-primary-500 text-background-50'
                      : i === 1
                        ? 'bg-accent-500 text-background-50'
                        : i === 2
                          ? 'bg-secondary-500 text-foreground-800'
                          : 'bg-background-200 text-foreground-500'
                  }`}
                >
                  {i + 1}
                </span>
                <span className="relative h-20 w-14 shrink-0 overflow-hidden rounded-md">
                  <img
                    src={c.cover}
                    alt={`${c.title}漫画封面`}
                    title={`${c.title}漫画在线看`}
                    className="h-full w-full object-cover object-top"
                  />
                </span>
                <div className="min-w-0 flex-1">
                  <h2 className="truncate text-base font-bold text-foreground-800 group-hover:text-primary-600">
                    {c.title}
                  </h2>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
                    <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-secondary-900">{c.genre}</span>
                    <span>{c.author}</span>
                    <span>{c.status}</span>
                  </div>
                </div>
                <div className="hidden flex-col items-end gap-1 sm:flex">
                  {tab === 'rating' || tab === 'finished' ? (
                    <span className="flex items-center gap-1 text-sm font-bold text-accent-600">
                      <i className="ri-star-fill text-xs" />
                      {c.rating}
                    </span>
                  ) : (
                    <span className="text-sm font-bold text-primary-600">{c.views}</span>
                  )}
                  <span className="text-xs text-foreground-400">{c.chapters}话</span>
                </div>
                <span className="whitespace-nowrap rounded-full bg-primary-100 px-3 py-1.5 text-xs font-medium text-primary-700 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                  去看
                </span>
              </Link>
            </li>
          ))}
        </ol>
      </section>
    </>
  );
}